import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2, Clock, LayoutGrid, List, AlertTriangle, Edit2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SHORT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 19 }, (_, i) => i + 5); // 5am to 11pm

// Get current date/time in WAT (UTC+1)
const getWATDate = () => {
  const now = new Date();
  return new Date(now.getTime() + (1 * 60 * 60 * 1000) + (now.getTimezoneOffset() * 60 * 1000));
};

const SHOW_COLORS = ['bg-[#D4AF37]/20 border-[#D4AF37]/40', 'bg-blue-500/20 border-blue-500/40', 'bg-emerald-500/20 border-emerald-500/40', 'bg-purple-500/20 border-purple-500/40', 'bg-red-500/20 border-red-500/40', 'bg-amber-500/20 border-amber-500/40', 'bg-cyan-500/20 border-cyan-500/40', 'bg-pink-500/20 border-pink-500/40'];
const SHOW_TEXT = ['text-[#D4AF37]', 'text-blue-400', 'text-emerald-400', 'text-purple-400', 'text-red-400', 'text-amber-400', 'text-cyan-400', 'text-pink-400'];

export default function AdminSchedule() {
  const [schedule, setSchedule] = useState([]);
  const [shows, setShows] = useState([]);
  const [selectedDay, setSelectedDay] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [form, setForm] = useState({ show_id: '', day_of_week: 0, start_time: '06:00', end_time: '08:00' });
  const [conflicts, setConflicts] = useState([]);
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const [editingEntryId, setEditingEntryId] = useState(null);

  const fetchData = () => {
    Promise.all([
      axios.get(`${API}/schedule`),
      axios.get(`${API}/shows`)
    ]).then(([s, sh]) => {
      setSchedule(s.data.schedule || []);
      setShows(sh.data.shows || []);
    }).catch(() => {});
  };

  useEffect(() => { fetchData(); }, []);

  // Build show color map
  const showColorMap = useMemo(() => {
    const map = {};
    const uniqueShows = [...new Set(schedule.map(e => e.show_id))];
    uniqueShows.forEach((sid, i) => { map[sid] = i % SHOW_COLORS.length; });
    return map;
  }, [schedule]);

  const dayEntries = schedule.filter(e => e.day_of_week === selectedDay).sort((a, b) => a.start_time.localeCompare(b.start_time));

  // Conflict check when form changes
  useEffect(() => {
    if (!showForm || !form.start_time || !form.end_time) { setConflicts([]); return; }
    const timer = setTimeout(() => {
      axios.post(`${API}/admin/schedule/check-conflicts`, {
        day_of_week: selectedDay,
        start_time: form.start_time,
        end_time: form.end_time,
        exclude_id: editingEntryId || undefined
      }).then(r => setConflicts(r.data.conflicts || [])).catch(() => setConflicts([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [showForm, selectedDay, form.start_time, form.end_time]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEntryId) {
        await axios.put(`${API}/admin/schedule/${editingEntryId}`, { ...form, day_of_week: selectedDay });
        toast.success('Schedule entry updated');
      } else {
        await axios.post(`${API}/admin/schedule`, { ...form, day_of_week: selectedDay });
        toast.success('Schedule entry created');
      }
      setShowForm(false);
      setEditingEntryId(null);
      setForm({ show_id: '', day_of_week: 0, start_time: '06:00', end_time: '08:00' });
      setConflicts([]);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleEdit = (entry) => {
    setEditingEntryId(entry.id);
    setForm({ show_id: entry.show_id, day_of_week: entry.day_of_week, start_time: entry.start_time, end_time: entry.end_time });
    setSelectedDay(entry.day_of_week);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setConfirmingDelete(id);
  };

  const executeDelete = async () => {
    if (!confirmingDelete) return;
    try { await axios.delete(`${API}/admin/schedule/${confirmingDelete}`); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed'); }
    setConfirmingDelete(null);
  };

  // Parse "HH:MM" to fractional hour
  const timeToHour = (t) => { const [h, m] = t.split(':').map(Number); return h + m / 60; };

  return (
    <div data-testid="admin-schedule">
      {/* Delete Confirmation Modal */}
      {confirmingDelete && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setConfirmingDelete(null)}>
          <div data-testid="delete-confirm-modal" className="bg-[#171A21] rounded-2xl border border-white/10 p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-500/10 mx-auto mb-4">
              <Trash2 size={22} className="text-red-400" />
            </div>
            <h3 className="text-white font-semibold text-lg text-center mb-2">Delete Schedule Entry</h3>
            <p className="text-[#94A3B8] text-sm text-center mb-6">Are you sure? This cannot be undone.</p>
            <div className="flex gap-2">
              <button data-testid="delete-cancel-btn" onClick={() => setConfirmingDelete(null)} className="flex-1 bg-white/5 text-white rounded-full py-2.5 hover:bg-white/10 text-sm">Cancel</button>
              <button data-testid="delete-confirm-btn" onClick={executeDelete} className="flex-1 bg-red-500 text-white font-semibold hover:bg-red-600 rounded-full py-2.5 text-sm">Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>Schedule</h1>
          <p className="text-[#94A3B8] text-sm mt-1">Manage weekly programming</p>
        </div>
        <div className="flex gap-2">
          {/* View Toggle */}
          <div className="bg-[#171A21] border border-white/5 rounded-xl flex p-0.5">
            <button data-testid="view-list" onClick={() => setViewMode('list')} className={`p-2.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-[#D4AF37] text-[#0F1115]' : 'text-[#94A3B8] hover:text-white'}`}>
              <List size={16} />
            </button>
            <button data-testid="view-grid" onClick={() => setViewMode('grid')} className={`p-2.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-[#D4AF37] text-[#0F1115]' : 'text-[#94A3B8] hover:text-white'}`}>
              <LayoutGrid size={16} />
            </button>
          </div>
          <button data-testid="add-schedule-button" onClick={() => { setEditingEntryId(null); setForm({ show_id: '', day_of_week: 0, start_time: '06:00', end_time: '08:00' }); setShowForm(!showForm); }} className="bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] rounded-full px-6 py-3 shadow-lg shadow-[#D4AF37]/20 flex items-center gap-2 active:scale-95">
            <Plus size={18} /> Add Entry
          </button>
        </div>
      </div>

      {/* Add Entry Form with Conflict Detection */}
      {showForm && (
        <div className="mb-8">
          {/* Conflict Warning */}
          {conflicts.length > 0 && (
            <div data-testid="conflict-warning" className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-4 flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-400 font-semibold text-sm">Time Conflict Detected</p>
                <p className="text-[#94A3B8] text-xs mt-1">
                  Overlaps with: {conflicts.map(c => `${c.show_name} (${c.start_time}–${c.end_time})`).join(', ')}
                </p>
              </div>
            </div>
          )}
          <form data-testid="schedule-form" onSubmit={handleSubmit} className="bg-[#171A21] rounded-2xl border border-white/5 p-6 space-y-4">
            <p className="text-white font-medium">{editingEntryId ? 'Edit' : 'New'} entry for <span className="text-[#D4AF37]">{DAYS[selectedDay]}</span></p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Show *</label>
                <select data-testid="schedule-show-select" value={form.show_id} onChange={e => setForm({...form, show_id: e.target.value})} className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] outline-none w-full" required>
                  <option value="">Select show...</option>
                  {shows.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Start Time</label>
                <input data-testid="schedule-start-time" type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] outline-none w-full" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">End Time</label>
                <input data-testid="schedule-end-time" type="time" value={form.end_time} onChange={e => setForm({...form, end_time: e.target.value})} className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] outline-none w-full" />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => { setShowForm(false); setEditingEntryId(null); setConflicts([]); }} className="bg-white/5 text-white hover:bg-white/10 border border-white/10 rounded-full px-6 py-2.5">Cancel</button>
              <button data-testid="schedule-submit-button" type="submit" className={`font-semibold rounded-full px-6 py-2.5 flex items-center gap-2 ${conflicts.length > 0 ? 'bg-amber-500 text-[#0F1115] hover:bg-amber-400' : 'bg-[#D4AF37] text-[#0F1115] hover:bg-[#E8C24D]'}`}>
                {conflicts.length > 0 && <AlertTriangle size={16} />}
                {editingEntryId ? 'Save Changes' : 'Add Entry'}{conflicts.length > 0 ? ' (Overlap)' : ''}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ═══════ GRID VIEW ═══════ */}
      {viewMode === 'grid' && (
        <div data-testid="schedule-grid-view" className="bg-[#171A21] rounded-2xl border border-white/5 overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              {/* Day Headers */}
              <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-white/5">
                <div className="p-3 text-xs text-[#94A3B8]/40 text-center">WAT</div>
                {DAYS.map((day, i) => {
                  const watNow = getWATDate();
                  const isToday = (watNow.getDay() === 0 ? 6 : watNow.getDay() - 1) === i;
                  return (
                    <div key={day} className={`p-3 text-center text-xs font-semibold ${isToday ? 'text-[#D4AF37] bg-[#D4AF37]/5' : 'text-[#94A3B8]'}`}>
                      {SHORT_DAYS[i]} {isToday && <span className="text-[8px] ml-1 opacity-70">TODAY</span>}
                    </div>
                  );
                })}
              </div>

              {/* Time Grid */}
              <div className="relative">
                {HOURS.map(hour => (
                  <div key={hour} className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-white/[0.03]" style={{ height: '48px' }}>
                    <div className="p-1 text-[10px] text-[#94A3B8]/40 text-right pr-2 pt-0.5 font-mono">
                      {String(hour).padStart(2, '0')}:00
                    </div>
                    {DAYS.map((_, dayIdx) => (
                      <div key={dayIdx} className="border-l border-white/[0.03] relative" />
                    ))}
                  </div>
                ))}

                {/* Schedule Blocks */}
                {schedule.map((entry) => {
                  const startH = timeToHour(entry.start_time);
                  const endH = timeToHour(entry.end_time);
                  const minHour = HOURS[0];
                  const maxHour = HOURS[HOURS.length - 1] + 1;
                  if (endH <= minHour || startH >= maxHour) return null;
                  const clampStart = Math.max(startH, minHour);
                  const clampEnd = Math.min(endH, maxHour);
                  const top = ((clampStart - minHour) / (maxHour - minHour)) * 100;
                  const height = ((clampEnd - clampStart) / (maxHour - minHour)) * 100;
                  const left = ((entry.day_of_week + 1) / 8) * 100;
                  const colorIdx = showColorMap[entry.show_id] || 0;

                  return (
                    <div
                      key={entry.id}
                      data-testid={`grid-block-${entry.id}`}
                      className={`absolute rounded-lg border ${SHOW_COLORS[colorIdx]} overflow-hidden cursor-pointer hover:brightness-125 transition-all`}
                      style={{
                        top: `${top}%`,
                        height: `${Math.max(height, 2)}%`,
                        left: `calc(${left}% + 2px)`,
                        width: `calc(${100 / 8}% - 4px)`,
                      }}
                      title={`${entry.show?.name || 'Show'} | ${entry.start_time}–${entry.end_time}`}
                    >
                      <div className="p-1.5 h-full flex flex-col">
                        <p className={`text-[10px] font-semibold truncate ${SHOW_TEXT[colorIdx]}`}>{entry.show?.name}</p>
                        <p className="text-[9px] text-[#94A3B8]/70 font-mono">{entry.start_time}-{entry.end_time}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ LIST VIEW ═══════ */}
      {viewMode === 'list' && (
        <>
          {/* Day Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {DAYS.map((day, i) => (
              <button key={day} data-testid={`admin-day-${i}`} onClick={() => setSelectedDay(i)}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap shrink-0 ${
                  selectedDay === i ? 'bg-[#D4AF37] text-[#0F1115]' : 'bg-[#171A21] text-[#94A3B8] hover:bg-white/10 border border-white/5'
                }`}>{day}</button>
            ))}
          </div>

          {/* Entries */}
          <div className="space-y-2">
            {dayEntries.length === 0 ? (
              <div className="text-center py-16 bg-[#171A21] rounded-2xl border border-white/5">
                <Clock size={32} className="mx-auto text-[#94A3B8]/40 mb-2" />
                <p className="text-[#94A3B8]">No entries for {DAYS[selectedDay]}</p>
              </div>
            ) : (
              dayEntries.map((entry, i) => {
                const colorIdx = showColorMap[entry.show_id] || 0;
                return (
                  <div key={entry.id} data-testid={`admin-schedule-entry-${i}`} className="bg-[#171A21] rounded-xl border border-white/5 p-4 flex items-center gap-4">
                    <div className={`rounded-lg p-3 shrink-0 ${SHOW_COLORS[colorIdx]}`}>
                      <Clock size={18} className={SHOW_TEXT[colorIdx]} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium">{entry.show?.name || 'Unknown'}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-[#94A3B8] text-sm font-mono">{entry.start_time} - {entry.end_time}</span>
                      </div>
                    </div>
                    {entry.host && <span className="text-xs text-[#D4AF37] hidden sm:block">{entry.host.name}</span>}
                    <button data-testid={`edit-schedule-${i}`} onClick={() => handleEdit(entry)} className="p-2 rounded-lg hover:bg-white/10 text-[#94A3B8] hover:text-white shrink-0">
                      <Edit2 size={16} />
                    </button>
                    <button data-testid={`delete-schedule-${i}`} onClick={() => handleDelete(entry.id)} className="p-2 rounded-lg hover:bg-red-500/20 text-[#94A3B8] hover:text-red-400 shrink-0">
                      <Trash2 size={16} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}
